import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface UserProfile {
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    profile?: {
        bio: string;
        profile_picture: string;
        phone_number: string;
    };
}

const MyProfilePage: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [bio, setBio] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [profilePicture, setProfilePicture] = useState('');

    // Password change fields
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const response = await api.get('/users/me/');
            const data = response.data;
            setProfile(data);
            setFirstName(data.first_name || '');
            setLastName(data.last_name || '');
            setEmail(data.email || '');
            setBio(data.profile?.bio || '');
            setPhoneNumber(data.profile?.phone_number || '');
            setProfilePicture(data.profile?.profile_picture || '');
            setLoading(false);
        } catch (error) {
            console.error('Error fetching profile', error);
            setMessage({ type: 'error', text: 'Não foi possível carregar o perfil.' });
            setLoading(false);
        }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                setMessage({ type: 'error', text: 'A imagem deve ter no máximo 2MB.' });
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePicture(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            await api.patch('/users/me/', {
                first_name: firstName,
                last_name: lastName,
                email: email,
                profile: {
                    bio: bio,
                    profile_picture: profilePicture,
                    phone_number: phoneNumber
                }
            });
            setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
            fetchProfile();
        } catch (error) {
            setMessage({ type: 'error', text: 'Erro ao salvar o perfil.' });
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'As senhas não coincidem.' });
            return;
        }
        if (newPassword.length < 6) {
            setMessage({ type: 'error', text: 'A nova senha deve ter pelo menos 6 caracteres.' });
            return;
        }
        setSaving(true);
        setMessage(null);
        try {
            await api.patch('/users/me/', {
                password: newPassword
            });
            setMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            setMessage({ type: 'error', text: 'Erro ao alterar a senha.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Carregando...</div>;

    return (
        <div className="page-container">
            <div className="page-header">
                <h1>Meu Perfil</h1>
            </div>

            {message && (
                <div style={{
                    padding: '10px 15px',
                    marginBottom: '20px',
                    borderRadius: '4px',
                    backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: message.type === 'success' ? '#155724' : '#721c24'
                }}>
                    {message.text}
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="card">
                    <h2 style={{ marginBottom: '20px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                        Informações Pessoais
                    </h2>
                    <form onSubmit={handleSaveProfile}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '20px' }}>
                            <div
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--bg-hover)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid var(--border-color)'
                                }}
                            >
                                {profilePicture ? (
                                    <img src={profilePicture} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <span style={{ color: 'var(--text-light)', fontSize: '2rem' }}>{profile?.username.charAt(0).toUpperCase()}</span>
                                )}
                            </div>
                            <div>
                                <label className="btn btn-outline" style={{ cursor: 'pointer' }}>
                                    Alterar Foto
                                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                                </label>
                                <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '5px' }}>Máx. 2MB (JPG, PNG)</p>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Nome do Usuário</label>
                            <input type="text" value={profile?.username} disabled style={{ backgroundColor: 'var(--bg-hover)' }} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div className="form-group">
                                <label>Nome</label>
                                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>Sobrenome</label>
                                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>E-mail</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Telefone</label>
                            <input type="text" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="(11) 98765-4321" />
                        </div>

                        <div className="form-group">
                            <label>Biografia</label>
                            <textarea
                                value={bio}
                                onChange={e => setBio(e.target.value)}
                                rows={4}
                                placeholder="Fale um pouco sobre você..."
                                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)' }}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button type="submit" className="btn" disabled={saving}>
                                {saving ? 'Salvando...' : 'Salvar Perfil'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="card" style={{ height: 'fit-content' }}>
                    <h2 style={{ marginBottom: '20px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                        Alterar Senha
                    </h2>
                    <form onSubmit={handleChangePassword}>
                        <div className="form-group">
                            <label>Nova Senha</label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Confirmar Nova Senha</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button type="submit" className="btn" style={{ backgroundColor: 'var(--accent-red)', color: 'white' }} disabled={saving}>
                                {saving ? 'Alterando...' : 'Alterar Senha'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MyProfilePage;
